/**
 * Mail HTML sanitizer for the HTML mail viewer (DESIGN.md §5 "Rich content", T10).
 *
 * Defense in depth: DOMPurify removes scripts/handlers, this module strips remote resources
 * unless allowed, and the viewer renders the result in a sandboxed iframe with a strict CSP.
 * The mail's own <style> blocks are kept (neutralised by `sanitizeMailCss`) and placed at the
 * start of the body; they apply inside the iframe only, so they cannot restyle the app.
 *
 * Theming: mails without their own colors are rendered on the app surface (token values passed
 * in as `MailTheme`), so they follow light/dark. Mails that set their own colors keep the light
 * "paper" they were designed for, so author colors never end up unreadable on a dark surface.
 */
import DOMPurify from 'dompurify';

export interface SanitizeOptions {
  /** Load remote images/backgrounds (default: blocked, DESIGN.md T10). */
  allowRemote?: boolean;
}

export interface SanitizedMail {
  html: string;
  /** True when at least one remote resource was removed. */
  blockedRemote: boolean;
  /** True when the mail sets its own text or background colors (inline, attributes or <style>). */
  ownColors: boolean;
}

const REMOTE_URL = /^\s*(https?:)?\/\//i;
const CSS_REMOTE_URL = /url\(\s*(['"]?)\s*(https?:)?\/\/[^)]*\)/gi;
const URL_ATTRIBUTES = ['src', 'srcset', 'background', 'poster'] as const;
/** Legacy HTML color attributes (font color, table bgcolor, body text/link colors). */
const COLOR_ATTRIBUTES = ['color', 'bgcolor', 'text', 'link', 'vlink', 'alink'] as const;
/** A color/background declaration in a <style> block. */
const CSS_COLOR_DECLARATION = /(^|[{;\s])(color|background(-color)?)\s*:/i;
/** Declared values that do not paint anything. */
const NO_COLOR = new Set(['', 'initial', 'inherit', 'unset', 'transparent', 'currentcolor', 'none', 'rgba(0, 0, 0, 0)']);

function setsColor(node: Element): boolean {
  if (COLOR_ATTRIBUTES.some((attribute) => node.hasAttribute(attribute))) return true;
  const style = (node as Partial<ElementCSSInlineStyle>).style;
  if (!style) return false;
  return [style.color, style.backgroundColor].some((value) => !NO_COLOR.has((value ?? '').trim().toLowerCase()));
}

/** `@import` rules (external stylesheets) and legacy script-in-CSS constructs. */
const CSS_IMPORT = /@import\b[^;]*;?/gi;
const CSS_LEGACY_SCRIPT = /expression\s*\(|behaviou?r\s*:|-moz-binding\s*:|javascript:/gi;
/** `position: fixed` would pin mail content over the viewer; the mail flows in the page instead. */
const CSS_FIXED = /(position\s*:\s*)fixed\b/gi;
/** A <style> block, for removing the HTML comment wrapper Outlook puts around its CSS. */
const STYLE_BLOCK = /(<style\b[^>]*>)([\s\S]*?)(<\/style\s*>)/gi;
/** Characters that must never appear in CSS placed into a <style> element of the srcdoc. */
const CSS_BREAKOUT = /<\/?\s*style|<!--|-->/gi;
/** Safe legacy `bgcolor` value (name or hex), carried from <body> into a CSS rule. */
const SAFE_COLOR = /^#?[\w]+$/;

interface CssResult {
  css: string;
  blockedRemote: boolean;
}

/**
 * Neutralises mail CSS (both <style> blocks and style attributes): no external stylesheets,
 * no remote url() unless allowed, no fixed positioning, no legacy script hooks. The iframe CSP
 * blocks remote loads as well; stripping them here lets the remote-images banner tell the user.
 */
export function sanitizeMailCss(css: string, { allowRemote = false }: SanitizeOptions = {}): CssResult {
  let blockedRemote = false;
  let clean = css.replace(CSS_BREAKOUT, '').replace(CSS_IMPORT, '').replace(CSS_LEGACY_SCRIPT, '').replace(CSS_FIXED, '$1absolute');
  if (!allowRemote) {
    const stripped = clean.replace(CSS_REMOTE_URL, 'none');
    blockedRemote = stripped !== clean;
    clean = stripped;
  }
  return { css: clean, blockedRemote };
}

/**
 * Outlook wraps CSS in `<!-- … -->`, which DOMPurify (correctly) treats as markup inside a raw
 * text element and drops the whole <style>. CSS ignores these tokens anyway, so remove them.
 */
function unwrapStyleComments(html: string): string {
  return html.replace(STYLE_BLOCK, (_match, open: string, css: string, close: string) => `${open}${css.replace(/<!--|-->/g, '')}${close}`);
}

export function sanitizeMailHtml(html: string, { allowRemote = false }: SanitizeOptions = {}): SanitizedMail {
  const purify = DOMPurify(window);
  let blockedRemote = false;
  let ownColors = false;
  const cleanCss = (css: string) => {
    const result = sanitizeMailCss(css, { allowRemote });
    blockedRemote ||= result.blockedRemote;
    return result.css;
  };

  purify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'style' && CSS_COLOR_DECLARATION.test(node.textContent ?? '')) ownColors = true;
  });

  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (!allowRemote) {
      for (const attribute of URL_ATTRIBUTES) {
        const value = node.getAttribute(attribute);
        if (value && (attribute === 'srcset' || REMOTE_URL.test(value))) {
          node.removeAttribute(attribute);
          blockedRemote = true;
        }
      }
    }
    const style = node.getAttribute('style');
    if (style) node.setAttribute('style', cleanCss(style));
    if (!ownColors && setsColor(node)) ownColors = true;
  });

  // Whole document: <style> blocks live in <head> (or lead the fragment, which the parser also
  // moves to <head>); a body-only sanitize would drop them and the mail would lose its layout.
  const dom = purify.sanitize(unwrapStyleComments(html), {
    WHOLE_DOCUMENT: true,
    RETURN_DOM: true,
    FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'iframe', 'object', 'embed', 'link', 'meta', 'base'],
    FORBID_ATTR: ['action', 'formaction'],
    ALLOW_DATA_ATTR: false,
  });

  // RETURN_DOM + WHOLE_DOCUMENT yields the <html> element.
  const root = dom as Element;
  const styles = Array.from(root.querySelectorAll('style'), (element) => {
    element.remove();
    return cleanCss(element.textContent ?? '');
  });
  const body = root.querySelector('body');
  // <body bgcolor/style> would be lost with the body element; carry it over as a body rule.
  const bodyRules = [body?.getAttribute('style') ?? '', SAFE_COLOR.test(body?.getAttribute('bgcolor') ?? '') ? `background-color:${body?.getAttribute('bgcolor')}` : '']
    .filter((rule) => rule && !/[{}]/.test(rule))
    .join(';');
  if (bodyRules) styles.push(`body{${bodyRules}}`);

  const css = styles.filter((block) => block.trim()).join('\n');
  const content = body?.innerHTML ?? '';
  return { html: css ? `<style>${css}</style>${content}` : content, blockedRemote, ownColors };
}

const ESCAPES: Readonly<Record<string, string>> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Escapes text for use in HTML content or attribute values. */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => ESCAPES[char] ?? char);
}

/**
 * Plain-text body → HTML for the viewer. Same shape as gromox's own text-to-HTML conversion (one
 * `<pre>`), which the viewer renders in the proportional app font with wrapped lines.
 */
export function plainTextToMailHtml(text: string): string {
  return `<pre>${escapeHtml(text)}</pre>`;
}

/** Resolved design-token values for the mail document (the sandboxed iframe cannot read the app's CSS). */
export interface MailTheme {
  colorScheme: 'light' | 'dark';
  background: string;
  foreground: string;
  mutedForeground: string;
  link: string;
  border: string;
  fontFamily: string;
}

/** Token values come from our own stylesheet; still refuse anything that could leave a declaration. */
const SAFE_CSS_VALUE = /^[\w\s#%.,()'"/+-]+$/;

function cssValue(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? '';
  return trimmed && SAFE_CSS_VALUE.test(trimmed) ? trimmed : fallback;
}

export interface MailDocumentOptions extends SanitizeOptions {
  /** App theme; omitted → neutral light system colors. */
  theme?: MailTheme;
  /** The mail brings its own colors: keep a light page instead of the themed surface. */
  ownColors?: boolean;
}

/** Base styles: the themed surface for normal mails, light system colors for mails with own colors. */
function baseStyles({ theme, ownColors }: MailDocumentOptions): string {
  const common =
    `body{margin:16px;font:14px/1.5 ${cssValue(theme?.fontFamily, 'system-ui,sans-serif')};overflow-wrap:anywhere}` +
    'img{max-width:100%;height:auto}' +
    // Matched search words (webmail search results).
    'mark{background:#fde047;color:#1c1917;border-radius:2px;padding:0 1px}' +
    // Text mails (a single <pre>, see plainTextToMailHtml) read in the proportional font, wrapped.
    'body>pre:only-child{font:inherit;white-space:pre-wrap;margin:0}';
  if (!theme || ownColors) return `:root{color-scheme:light}body{background:Canvas;color:CanvasText}${common}`;
  const border = cssValue(theme.border, 'GrayText');
  return (
    `:root{color-scheme:${theme.colorScheme === 'dark' ? 'dark' : 'light'}}` +
    `body{background:${cssValue(theme.background, 'Canvas')};color:${cssValue(theme.foreground, 'CanvasText')}}` +
    `a{color:${cssValue(theme.link, 'LinkText')}}` +
    `blockquote{margin:8px 0;padding-left:12px;border-left:2px solid ${border};color:${cssValue(theme.mutedForeground, 'GrayText')}}` +
    `hr{border:0;border-top:1px solid ${border}}` +
    `table,th,td{border-color:${border}}` +
    common
  );
}

/** Builds the iframe document: CSP first, base styles, then the sanitized body. */
export function buildMailDocument(body: string, options: MailDocumentOptions = {}): string {
  const imgSrc = options.allowRemote ? 'data: cid: https: http:' : 'data: cid:';
  const csp = `default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:; media-src 'none'`;
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${csp}">`,
    '<base target="_blank">',
    `<style>${baseStyles(options)}</style>`,
    '</head><body>',
    body,
    '</body></html>',
  ].join('');
}
